/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { Card, CardBody, Col, Container, Row, Button, Badge, Progress, Spinner } from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, RefreshCw, CheckCircle, XCircle, AlertCircle, Clock, SkipForward } from 'react-feather';
import { connect } from 'react-redux';
import Breadcrumbs from '../CommonElements/Breadcrumbs';
import { getBatch as getBatchAction, refillBatch as refillBatchAction } from '../ReduxToolkit/Reducers/queues';

function Queue({ getBatch, refillBatch, selectedBatch }) {
  const location = useLocation();
  const navigate = useNavigate();
  const queue = location.state?.queue;

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRefilling, setIsRefilling] = useState(false);

  useEffect(() => {
    if (!queue) {
      navigate('/queues');
    } else {
      fetchBatchData();
    }
  }, [queue]);


  const fetchBatchData = async (isAutoRefresh = false) => {
    if (!isAutoRefresh) setIsLoading(true);
    setIsRefreshing(isAutoRefresh);

    try {
      const payload = {
        batch_id: queue.batch_id,
        admin: queue.server?.toLowerCase() === 'admin'
      };

      await getBatch(payload);
    } catch (error) {
      console.error('Error fetching batch data:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefillBatch = async () => {
    setIsRefilling(true);
    try {
      const payload = {
        batch_id: queue.batch_id,
        admin: queue.server?.toLowerCase() === 'admin'
      };
      await refillBatch(payload);
    } catch (error) {
      console.error('Error refilling batch:', error);
    } finally {
      setIsRefilling(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return `${day} ${month} ${year} at ${time}`;
  };

  const parseNames = (namesString) => {
    try {
      return JSON.parse(namesString);
    } catch {
      return [];
    }
  };

  if (!queue) {
    return null;
  }

  if (isLoading) {
    return (
      <div className='page-body'>
        <Breadcrumbs pageTitle='Queue' parent='Queues' title='Batch Details' />
        <Container fluid>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
          </div>
        </Container>
      </div>
    );
  }

  const completionPercentage = selectedBatch ? (parseInt(selectedBatch.completed) / parseInt(selectedBatch.total)) * 100 : 0;
  const allNames = selectedBatch ? parseNames(selectedBatch.all_names) : [];
  const isProcessing = selectedBatch && (selectedBatch.pending_jobs > 0 || selectedBatch.active_jobs > 0);

  return (
    <div className='page-body'>
      <Breadcrumbs pageTitle='Queue' parent='Queues' title='Batch Details' />
      <Container fluid>
        <Row>
          <Col xs={12}>
            <Button
              color="link"
              className="ps-0 mb-3 d-flex align-items-center"
              onClick={() => navigate(-1)}
            >
              <ChevronLeft size={18} />
              <span className="ms-1">Back</span>
            </Button>
          </Col>
        </Row>

        {/* Batch Header Card */}
        <Row>
          <Col xs={12}>
            <Card className="mb-4">
              <CardBody>
                <Row className="align-items-center">
                  <Col>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h4 className="mb-0">Batch: {queue.batch_id}</h4>
                      <Badge color={isProcessing ? 'warning' : 'success'}>
                        {isProcessing ? 'Processing' : 'Completed'}
                      </Badge>
                      {queue.server && (
                        <Badge color="info">{queue.server}</Badge>
                      )}
                    </div>
                    <p className="text-muted mb-2">
                      <strong>Type:</strong> {queue.type || 'N/A'}
                    </p>
                    <p className="text-muted mb-2">
                      <strong>File:</strong> {selectedBatch?.input_file_name || 'N/A'}
                    </p>
                    <p className="text-muted mb-0">
                      <strong>Added:</strong> {formatDate(queue.added_at)}
                    </p>
                  </Col>
                  <Col xs="auto" className="d-flex flex-column align-items-end gap-2">
                    <div className="d-flex gap-2">
                      <Button
                        color="secondary"
                        outline
                        onClick={handleRefillBatch}
                        disabled={isRefilling}
                        className="d-flex align-items-center gap-2"
                      >
                        {isRefilling ? <Spinner size="sm" /> : <RefreshCw size={18} />}
                        Refill Batch
                      </Button>
                      <Button
                        color="primary"
                        outline
                        onClick={() => fetchBatchData()}
                        disabled={isRefreshing}
                        className="d-flex align-items-center gap-2"
                      >
                        <RefreshCw size={18} className={isRefreshing ? 'spin' : ''} />
                        Refresh
                      </Button>
                    </div>
                    {isProcessing && (
                      <small className="text-muted">
                        {queue.type} are still processing. Click refresh to update.
                      </small>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Progress Card */}
        {selectedBatch && (
          <Row>
            <Col xs={12}>
              <Card className="mb-4">
                <CardBody>
                  <h5 className="mb-3">Overall Progress</h5>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">
                        {selectedBatch.completed} of {selectedBatch.total} completed
                      </span>
                      <span className="text-muted">{Math.round(completionPercentage)}%</span>
                    </div>
                    <Progress
                      value={completionPercentage}
                      color={completionPercentage === 100 ? 'success' : 'primary'}
                      style={{ height: '24px' }}
                    />
                  </div>
                  <Row className="text-center mt-4 row-cols-2 row-cols-md-3 row-cols-lg-5 g-3">
                    <Col>
                      <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                        <Clock size={20} className="text-warning" />
                        <h3 className="mb-0">{selectedBatch.pending_jobs}</h3>
                      </div>
                      <p className="text-muted mb-0">Pending {queue.type}</p>
                    </Col>
                    <Col>
                      <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                        <AlertCircle size={20} className="text-info" />
                        <h3 className="mb-0">{selectedBatch.active_jobs}</h3>
                      </div>
                      <p className="text-muted mb-0">Active {queue.type}</p>
                    </Col>
                    <Col>
                      <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                        <CheckCircle size={20} className="text-success" />
                        <h3 className="mb-0">{selectedBatch.successful}</h3>
                      </div>
                      <p className="text-muted mb-0">Successful</p>
                    </Col>
                    <Col>
                      <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                        <XCircle size={20} className="text-danger" />
                        <h3 className="mb-0">{selectedBatch.failed}</h3>
                      </div>
                      <p className="text-muted mb-0">Failed</p>
                    </Col>
                    <Col>
                      <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                        <SkipForward size={20} className="text-secondary" />
                        <h3 className="mb-0">{selectedBatch.skipped ?? 0}</h3>
                      </div>
                      <p className="text-muted mb-0">Skipped</p>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Status Cards */}
        {selectedBatch && (
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <div className="mb-4" style={{ flex: 1 }}>
              <Card className="border-start border-primary border-4">
                <CardBody>
                  <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: '0.75rem' }}>
                    Total Items
                  </h6>
                  <h2 className="mb-0">{selectedBatch.total}</h2>
                </CardBody>
              </Card>
            </div>
            <div className="mb-4" style={{ flex: 1 }}>
              <Card className="border-start border-success border-4">
                <CardBody>
                  <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: '0.75rem' }}>
                    Completed
                  </h6>
                  <h2 className="mb-0 text-success">{selectedBatch.completed}</h2>
                </CardBody>
              </Card>
            </div>
            <div className="mb-4" style={{ flex: 1 }}>
              <Card className="border-start border-success border-4">
                <CardBody>
                  <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: '0.75rem' }}>
                    Success
                  </h6>
                  <h2 className="mb-0 text-success">{selectedBatch.successful}</h2>
                </CardBody>
              </Card>
            </div>
            <div className="mb-4" style={{ flex: 1 }}>
              <Card className="border-start border-warning border-4">
                <CardBody>
                  <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: '0.75rem' }}>
                    Duplicates
                  </h6>
                  <h2 className="mb-0 text-warning">{selectedBatch.duplicate}</h2>
                </CardBody>
              </Card>
            </div>
            <div className="mb-4" style={{ flex: 1 }}>
              <Card className="border-start border-secondary border-4">
                <CardBody>
                  <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: '0.75rem' }}>
                    Skipped
                  </h6>
                  <h2 className="mb-0 text-secondary">{selectedBatch.skipped ?? 0}</h2>
                </CardBody>
              </Card>
            </div>
            <div className="mb-4" style={{ flex: 1 }}>
              <Card className="border-start border-danger border-4">
                <CardBody>
                  <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: '0.75rem' }}>
                    Failed
                  </h6>
                  <h2 className="mb-0 text-danger">{selectedBatch.failed}</h2>
                </CardBody>
              </Card>
            </div>
          </div>
        )}

        {/* Items List */}
        {selectedBatch && allNames.length > 0 && (
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody>
                  <h5 className="mb-3">Items in Batch ({allNames.length})</h5>
                  <Row>
                    {allNames.map((name, index) => (
                      <Col xs={12} sm={6} md={4} lg={3} key={index} className="mb-3">
                        <div
                          className="p-3 border rounded d-flex align-items-center"
                          style={{ backgroundColor: '#f8f9fa' }}
                        >
                          <div className="me-2" style={{ fontSize: '1.2rem' }}>
                            {index + 1}.
                          </div>
                          <div style={{ flex: 1, overflow: 'hidden' }}>
                            <div
                              style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                              title={name}
                            >
                              {name}
                            </div>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

      </Container>

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

const mapStateToProps = (state) => ({
  selectedBatch: state.queues.selecteBatch,
});

const mapDispatchToProps = {
  getBatch: getBatchAction,
  refillBatch: refillBatchAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(Queue);
